import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, X, FileText, Download, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPE_MAP = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

const DEFAULT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploadWidget = ({
  module = 'ptw',
  referenceId = null,
  maxFiles = 5,
  maxSizeMB = 10,
  allowedExtensions = DEFAULT_EXTENSIONS,
  onFilesChange,
  existingFiles = [],
  disabled = false,
}) => {
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Refs to avoid stale closures when multiple files upload concurrently
  const existingFilesRef = useRef(existingFiles);
  existingFilesRef.current = existingFiles;
  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;

  const token = localStorage.getItem('token');
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const totalFiles = existingFiles.length + Object.keys(uploading).length;

  const validateFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      return `"${file.name}" - unsupported type. Allowed: ${allowedExtensions.join(', ').toUpperCase()}`;
    }
    if (file.size > maxSizeBytes) {
      return `"${file.name}" - exceeds ${maxSizeMB}MB limit (${formatFileSize(file.size)})`;
    }
    return null;
  };

  const uploadFile = useCallback(async (file) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const contentType = CONTENT_TYPE_MAP[ext] || file.type || 'application/octet-stream';

    setUploading(prev => ({ ...prev, [tempId]: { name: file.name, size: file.size, status: 'uploading' } }));
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (module) formData.append('module', module);
      if (referenceId) formData.append('reference_id', referenceId);

      const res = await axios.post(
        `${API}/files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [tempId]: pct }));
          },
        }
      );

      const file_id = res.data.id;

      // Done - remove from uploading state
      setUploading(prev => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });

      // Notify parent with the new file
      const newFile = {
        id: file_id,
        filename: file.name,
        content_type: contentType,
        file_size: file.size,
        status: 'available',
      };
      // Use ref to read latest files, avoiding stale closure with concurrent uploads
      const updatedFiles = [...existingFilesRef.current, newFile];
      existingFilesRef.current = updatedFiles; // Update ref immediately for other in-flight uploads
      if (onFilesChangeRef.current) {
        onFilesChangeRef.current(updatedFiles);
      }
    } catch (err) {
      console.error('File upload failed:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
      setErrors(prev => [...prev, `"${file.name}" - ${errorMsg}`]);
      setUploading(prev => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });
    }
  }, [token, module, referenceId, maxSizeBytes]);

  const handleFiles = useCallback((files) => {
    setErrors([]);
    const fileList = Array.from(files);

    // Check total count
    const currentCount = existingFiles.length + Object.keys(uploading).length;
    if (currentCount + fileList.length > maxFiles) {
      setErrors([`Cannot add ${fileList.length} file(s). Maximum ${maxFiles} files allowed (${currentCount} already attached).`]);
      return;
    }

    // Validate each file
    const validationErrors = [];
    const validFiles = [];
    for (const file of fileList) {
      const error = validateFile(file);
      if (error) {
        validationErrors.push(error);
      } else {
        validFiles.push(file);
      }
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
    }

    // Upload valid files
    for (const file of validFiles) {
      uploadFile(file);
    }
  }, [existingFiles, uploading, maxFiles, uploadFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }, [disabled, handleFiles]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleRemoveFile = (fileId) => {
    if (onFilesChange) {
      onFilesChange(existingFiles.filter(f => f.id !== fileId));
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const res = await axios.get(
        `${API}/files/${fileId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const link = document.createElement('a');
      link.href = res.data.download_url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('msword')) return '📝';
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return '📊';
    if (contentType?.includes('image/')) return '🖼️';
    return '📎';
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-gray-700">
        Attachments ({existingFiles.length}/{maxFiles})
      </label>

      {/* Drop Zone */}
      {!disabled && totalFiles < maxFiles && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {allowedExtensions.map(e => e.toUpperCase()).join(', ')} up to {maxSizeMB}MB each
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedExtensions.map(e => `.${e}`).join(',')}
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          {errors.map((err, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          ))}
          <button
            onClick={() => setErrors([])}
            className="text-xs text-red-500 hover:text-red-700 mt-1 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Uploading Files */}
      {Object.entries(uploading).map(([tempId, info]) => (
        <div key={tempId} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{info.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress[tempId] || 0}%` }}
                />
              </div>
              <span className="text-xs text-blue-700">
                {info.status === 'scanning' ? 'Scanning...' : `${uploadProgress[tempId] || 0}%`}
              </span>
            </div>
          </div>
        </div>
      ))}

      {/* Existing Files */}
      {existingFiles.map((file) => (
        <div key={file.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">
          <span className="text-lg flex-shrink-0">{getFileIcon(file.content_type)}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(file.file_size)}
              {file.uploaded_by && ` · ${file.uploaded_by}`}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {file.id && !file.id.startsWith('temp-') && (
              <button
                onClick={() => handleDownload(file.id, file.filename)}
                className="p-1.5 text-gray-500 hover:text-blue-600 rounded"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            {!disabled && (
              <button
                onClick={() => handleRemoveFile(file.id)}
                className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileUploadWidget;
